/**
 * Human-readable labels for the backend's `BusinessRole` values.
 *
 * Single source of truth for role display text — previously duplicated
 * (and drifted) across AccountMenu.tsx and AdminUsersPage.tsx, one of
 * which was missing `commercial` entirely (falling back to the raw
 * lowercase role string). Display labels are allowed to diverge from the
 * role value itself (`sales_rep` → "Sales rep"); the role value is a
 * stable backend identifier, not UI copy.
 */
// `satisfies` (rather than a `: Record<string, string>` annotation) validates
// the shape while preserving literal key inference, so `keyof typeof
// ROLE_LABELS` below is the real literal union, not plain `string`.
const ROLE_LABELS = {
  admin: "Admin",
  manager: "Manager",
  sales_rep: "Sales rep",
  csr: "CSR/AM",
  engineer: "Engineer",
  commercial: "Commercial",
  finance: "Finance",
  marketing: "Marketing",
  stock_control: "Stock control",
  pending: "Pending",
} satisfies Record<string, string>;

/** The backend's `BusinessRole` values, as a literal union. */
export type BusinessRole = keyof typeof ROLE_LABELS;

// Proves `BusinessRole` stayed a narrow literal union rather than widening to
// `string` (e.g. if `ROLE_LABELS` regresses to a `: Record<string, string>`
// annotation). Lives here, not in the test file, because only this file's
// `tsc` program actually runs in CI — the test file is typecheck-excluded.
type RoleUnionIsNarrow = string extends BusinessRole ? never : true;
export const ROLE_UNION_IS_NARROW: RoleUnionIsNarrow = true;

/**
 * Ordered list of the backend's `BusinessRole` values, for rendering role
 * pickers (e.g. the admin users role filter). Derived from `ROLE_LABELS` so
 * the two can never drift apart.
 */
export const BUSINESS_ROLES = Object.keys(
  ROLE_LABELS,
) as ReadonlyArray<BusinessRole>;

/** Display label for a role, falling back to the raw role string when unknown. */
export function roleLabel(role: string | null | undefined): string {
  if (role == null) return "";
  return (ROLE_LABELS as Record<string, string>)[role] ?? role;
}
